/**
 * Route guard for any page that requires a signed-in user.
 *
 * Redirect rules (in order):
 *   - query loading → render `LoadingShell`
 *   - not signed in → /sign-in
 *   - status === "pending_approval" → /pending-approval
 *   - status === "disabled" → /disabled
 *   - active → render children
 *
 * The `allowPending` and `allowDisabled` props are escape hatches
 * for the pages that *are* /pending-approval and /disabled — those
 * pages need to render for pending/disabled users without the guard
 * redirecting them back to themselves in a loop.
 */

import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { LoadingShell } from "@/components/LoadingShell";
import { useAuth } from "@/features/auth/useAuth";

interface Props {
  children: ReactNode;
  /** Set true on the pending-approval page so it doesn't redirect itself away. */
  allowPending?: boolean;
  /** Set true on the disabled page so it doesn't redirect itself away. */
  allowDisabled?: boolean;
}

export function RequireAuth({
  children,
  allowPending = false,
  allowDisabled = false,
}: Props) {
  const { isLoading, isAuthenticated, status } = useAuth();

  if (isLoading) {
    return <LoadingShell />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/sign-in" replace />;
  }

  if (status === "pending_approval" && !allowPending) {
    return <Navigate to="/pending-approval" replace />;
  }

  if (status === "disabled" && !allowDisabled) {
    return <Navigate to="/disabled" replace />;
  }

  return <>{children}</>;
}
